import {
  Button,
  Forbidden403,
  NotFound404,
  Result,
  Space,
  Typography,
  errorMessageFa,
  isApiError,
} from '@bookora/ui';
import { useEffect } from 'react';

import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';

import { env } from '@/lib/env';

import { SupportButton } from './support-contact';

/**
 * A lazily-loaded page chunk that no longer exists — almost always because a
 * deploy replaced the hashed assets under a tab that was already open. The
 * browser phrases it differently in each engine.
 */
const CHUNK_LOAD_ERROR =
  /dynamically imported module|Importing a module script failed|Loading chunk|Loading CSS chunk|error loading dynamically imported/i;

const CHUNK_RELOAD_FLAG = 'bookora_chunk_reload_at';

function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_LOAD_ERROR.test(error.message);
}

/**
 * The router's `errorElement`, mounted at the shell so one screen throwing
 * never blanks the whole panel — the sidebar, the clinic switcher and the
 * notification bell keep working while the content area explains itself.
 *
 * A 403 and a 404 are not "errors" to a user, so they get their own quiet
 * screens; everything else gets a recoverable one with a reload.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const staleChunk = isChunkLoadError(error);

  // After a deploy, reload ONCE to pick up the new asset manifest. The flag
  // (with a time window) stops a genuinely broken chunk from reload-looping.
  useEffect(() => {
    if (!staleChunk) return;
    try {
      const last = Number(sessionStorage.getItem(CHUNK_RELOAD_FLAG) ?? 0);
      if (Date.now() - last < 30_000) return;
      sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(Date.now()));
    } catch {
      return;
    }
    window.location.reload();
  }, [staleChunk]);

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) return <NotFound404 />;
    if (error.status === 403) return <Forbidden403 />;
  }

  if (isApiError(error)) {
    if (error.statusCode === 404) return <NotFound404 />;
    if (error.isPermissionDenied) return <Forbidden403 detail={error.message} />;
  }

  if (staleChunk) {
    return (
      <Result
        status="info"
        title="نسخهٔ جدیدی از پنل منتشر شده"
        subTitle="برای باز شدن این صفحه، پنل رو دوباره بارگذاری کنید."
        extra={
          <Button type="primary" onClick={() => window.location.reload()}>
            بارگذاری دوباره
          </Button>
        }
      />
    );
  }

  return (
    <Result
      status="error"
      title="این صفحه باز نشد"
      subTitle={
        <>
          {errorMessageFa(error)}
          <br />
          اگر صفحه باز نشد، به داشبورد برگردید و از منو دوباره این بخش رو باز کنید. اگر مشکل ادامه
          داشت، با پشتیبانی تماس بگیرید.
        </>
      }
      extra={
        <Space wrap>
          <Button type="primary" onClick={() => navigate(0)}>
            بارگذاری دوباره
          </Button>
          {/* One control, not a `<Button>` inside a `<Link>`: that nesting is
              two tab stops and a doubled focus ring. */}
          <Button onClick={() => navigate('/')}>بازگشت به داشبورد</Button>
          {/* The last resort names a person to call, not just another page. */}
          <SupportButton />
        </Space>
      }
    >
      {env.isDev && error instanceof Error && error.stack ? (
        <Typography.Paragraph>
          <pre
            className="bk-ltr-island"
            style={{
              margin: 0,
              maxHeight: 240,
              overflow: 'auto',
              fontSize: 'var(--bk-text-caption)',
              whiteSpace: 'pre-wrap',
            }}
          >
            {error.stack}
          </pre>
        </Typography.Paragraph>
      ) : null}
    </Result>
  );
}
